import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabaseClient';

const CustomerDetailsModal = ({ customer, onClose }) => {
  if (!customer) return null;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [recentOrders, setRecentOrders] = useState([]);
  const [latestOrder, setLatestOrder] = useState(null);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        let query = supabase
          .from('orders')
          .select('id, order_number, order_date, total_amount, status, customer_id, phone, customer_name, address, pickup_type, custom_instructions, internal_notes, kakao_notification, order_items ( id )')
          .order('order_date', { ascending: false })
          .limit(5);

        if (customer?.customerId) {
          query = query.eq('customer_id', customer.customerId);
        } else if (customer?.phone) {
          query = query.eq('phone', customer.phone);
        } else if (customer?.name) {
          query = query.eq('customer_name', customer.name);
        }

        const { data, error } = await query;

        if (error) throw error;
        if (!mounted) return;

        const rows = data || [];
        setRecentOrders(rows);
        setLatestOrder(rows?.[0] || null);
      } catch (e) {
        if (!mounted) return;
        setRecentOrders([]);
        setLatestOrder(null);
        setErrorMessage(e?.message || '고객 상세 정보를 불러오지 못했습니다.');
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [customer?.customerId, customer?.phone, customer?.name]);

  const avgOrderAmount = useMemo(() => {
    const totalOrders = Number(customer?.totalOrders || 0);
    if (!totalOrders) return 0;
    return Math.round(Number(customer?.totalSpent || 0) / totalOrders);
  }, [customer?.totalOrders, customer?.totalSpent]);

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const preferences = useMemo(() => {
    return {
      pickupType: latestOrder?.pickup_type || '-',
      preferredTime: '-',
      kakaoNotification:
        latestOrder?.kakao_notification === true
          ? '동의'
          : latestOrder?.kakao_notification === false
            ? '미동의'
            : '-',
      specialInstructions: latestOrder?.custom_instructions || '-',
    };
  }, [latestOrder]);

  const adminMemo = useMemo(() => {
    return latestOrder?.internal_notes || '-';
  }, [latestOrder]);

  const shippingAddress = useMemo(() => {
    return latestOrder?.address || customer?.address || '-';
  }, [latestOrder?.address, customer?.address]);

  const getOrderStatusColor = (status) => {
    switch (status) {
      case '완료':
        return 'bg-success/10 text-success';
      case '배송 예정':
      case '세탁 중':
      case '픽업 예정':
        return 'bg-accent/10 text-accent';
      case '주문 접수':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-card shadow-card">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">고객 상세 정보</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="닫기"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6">
          {errorMessage && (
            <div className="mb-6 rounded-lg bg-error/10 p-4">
              <p className="text-sm text-error">{errorMessage}</p>
            </div>
          )}

          <div className="mb-6 flex items-start space-x-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Icon name="User" size={32} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-foreground">{customer?.name}</h3>
              <p className="text-sm text-muted-foreground">{customer?.phone}</p>
              <div className="mt-2 flex items-center space-x-2">
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                  customer?.status === 'active' ? 'bg-success/10 text-success' :
                  customer?.status === 'vip'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  {customer?.status === 'active' ? '활성' : customer?.status === 'vip' ? 'VIP' : '비활성'}
                </span>
                <span className="text-xs text-muted-foreground">
                  가입일: {customer?.registrationDate}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">총 주문</p>
              <p className="mt-1 text-2xl font-bold text-accent">{customer?.totalOrders}건</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">총 결제액</p>
              <p className="mt-1 text-2xl font-bold text-foreground">
                {customer?.totalSpent?.toLocaleString('ko-KR')}원
              </p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">평균 주문액</p>
              <p className="mt-1 text-2xl font-bold text-foreground">
                {avgOrderAmount ? avgOrderAmount?.toLocaleString('ko-KR') : 0}원
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="mb-3 flex items-center space-x-2 text-sm font-semibold text-foreground">
              <Icon name="MapPin" size={16} />
              <span>배송 정보</span>
            </h4>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-foreground">{shippingAddress}</p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="mb-3 flex items-center space-x-2 text-sm font-semibold text-foreground">
              <Icon name="Settings" size={16} />
              <span>서비스 선호도</span>
            </h4>
            <div className="space-y-2 rounded-lg bg-muted/50 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">픽업 방식</span>
                <span className="text-foreground">{preferences?.pickupType}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">선호 시간</span>
                <span className="text-foreground">{preferences?.preferredTime}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">카카오톡 알림</span>
                <span className="text-foreground">{preferences?.kakaoNotification}</span>
              </div>
              <div className="flex items-start justify-between text-sm">
                <span className="text-muted-foreground">특이사항</span>
                <span className="text-right text-foreground">{preferences?.specialInstructions}</span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="mb-3 flex items-center space-x-2 text-sm font-semibold text-foreground">
              <Icon name="Package" size={16} />
              <span>최근 주문 내역</span>
            </h4>
            <div className="space-y-2">
              {recentOrders?.map((order) => (
                <div key={order?.id} className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
                  <div>
                    <p className="font-medium text-foreground">
                      {order?.order_number || `#${order?.id}`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(order?.order_date)} · {(order?.order_items?.length || 0)}개 항목
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-foreground">
                      {Number(order?.total_amount || 0)?.toLocaleString('ko-KR')}원
                    </p>
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getOrderStatusColor(order?.status)}`}>
                      {order?.status}
                    </span>
                  </div>
                </div>
              ))}

              {!isLoading && recentOrders?.length === 0 && (
                <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
                  최근 주문 내역이 없습니다.
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="mb-3 flex items-center space-x-2 text-sm font-semibold text-foreground">
              <Icon name="FileText" size={16} />
              <span>관리자 메모</span>
            </h4>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">{adminMemo}</p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-border bg-card px-6 py-4">
          <Button
            variant="default"
            iconName="Download"
            fullWidth
            onClick={() => {
              window.print();
            }}
            disabled={isLoading}
          >
            PDF 다운로드
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;